






import {
  Vue,
  Component,
  Prop
} from "@/shared/vuepack";
import { Location } from "vue-router";
import * as SharedRoutes from "@/shared/route-names";

import {
  DrivewealthAccountManagementType
} from "@/shared/types";
import { useUserStore } from "@/shared/store/user";
import { useDrivewealthStore } from "@/shared/store/drivewealth";
import { useInvestmentThresholdStore } from "@/shared/store/investmentThreshold";
import { useFeatureFlagStore } from "@/shared/store/featureFlag";

@Component({
  components: {}
})
export default class DrivewealthNavigation extends Vue {
  userStore = useUserStore();
  drivewealthStore = useDrivewealthStore();
  investmentThresholdStore = useInvestmentThresholdStore();
  featureFlagStore = useFeatureFlagStore();

  @Prop({ required: false })
  username: string;

  @Prop({ required: false })
  partnerId: number;

  @Prop({ required: false })
  accountId: number;

  get useMultiAccountRoutes (): boolean {
    return !!this.accountId;
  }

  get routeParams (): { [key: string]: string } {
    const routeParams: { [key: string]: string } = {};

    if (this.accountId) {
      routeParams.accountId = `${this.accountId}`;
    }

    if (this.partnerId) {
      routeParams.partnerId = `${this.partnerId}`;
    }

    return routeParams;
  }

  get navItems () {
    const query = (this.username) ? { username: this.username } : undefined;
    const items = new Map<Location, string>();
    const drivewealthPerformanceEnabled = this.featureFlagStore.flags.bend25653EnableDrivewealthPerformance;

    if (drivewealthPerformanceEnabled) {
      items.set({
        name: SharedRoutes.DrivewealthAccountSummary,
        params: this.routeParams,
        query
      }, "Account Summary");
      items.set({
        name: SharedRoutes.DrivewealthCurrentHoldings,
        params: this.routeParams,
        query
      }, "Current Holdings");
    } else {
      items.set({
        name: SharedRoutes.DrivewealthInvestmentAccountV2,
        params: this.routeParams,
        query
      }, "Current Holdings");
    }

    if (this.showAvailableInvestmentsTab) {
      if (this.useMultiAccountRoutes) {
        items.set({
          name: SharedRoutes.DrivewealthAvailableInvestmentsV2,
          params: this.routeParams,
          query
        }, "Search & Trade");
      } else {
        items.set({ name: SharedRoutes.DrivewealthAvailableInvestments, query }, "Search & Trade");
      }
    }

    if (this.drivewealthStore.isAccountManagementType(DrivewealthAccountManagementType.RiaManaged, this.accountId)) {
      // Managed accounts should route to the activity/transfers 2nd level tab
      if (this.useMultiAccountRoutes) {
          items.set({
            name: SharedRoutes.DrivewealthInvestmentActivityTransfersV2,
            params: this.routeParams,
            query
          }, "Activity");
        } else {
          items.set({
            name: SharedRoutes.DrivewealthInvestmentActivityTransfers,
            query
          }, "Activity");
        }

    } else if (this.drivewealthStore.managedTransfersActivity.length > 0 && !this.useMultiAccountRoutes) {
      // Non-managed accounts that have previous managed transfers should route to the activity/history 2nd level tab
      // Multi-account users should not enter here

      if (this.userStore.isEmployeePortal) {
        items.set({
          name: SharedRoutes.DrivewealthInvestmentActivityHistory,
          query
        }, "Activity");
      }
      if (this.userStore.isEmployerPortal) {
        items.set({
          name: SharedRoutes.DrivewealthInvestmentActivityHistory,
          params: { partnerId: `${this.partnerId}` },
          query
        }, "Activity");
      }
    } else {
      // All other accounts should route to the root activity page with no 2nd level tabs
      if (this.useMultiAccountRoutes) {
        items.set({
          name: SharedRoutes.DrivewealthInvestmentActivityV2,
          params: this.routeParams,
          query
        }, "Activity");
      } else {
        items.set({ name: SharedRoutes.DrivewealthInvestmentActivity, query }, "Activity");
      }
    }

    if (this.useMultiAccountRoutes) {
      items.set({
        name: SharedRoutes.DrivewealthInvestmentDocumentsV2,
        params: {...this.routeParams, pageNumber: "1"},
        query
      }, "Documents");
    } else {
      items.set({ name: SharedRoutes.DrivewealthInvestmentDocuments, query }, "Documents");
    }

    if (this.useMultiAccountRoutes) {
      items.set({
        name: SharedRoutes.DrivewealthInvestmentFeesV2,
        params: this.routeParams,
        query
      }, "Fees");
    }

    if (this.userStore.isEmployerPortal) {
      if (this.useMultiAccountRoutes) {
        items.set({
          name: SharedRoutes.DrivewealthInvestmentDayTradesV2,
          params: this.routeParams,
          query
        }, "Day Trades");
      } else {
        items.set({
          name: SharedRoutes.DrivewealthInvestmentDayTrades,
          params: this.routeParams,
          query
        }, "Day Trades");
      }
    }

    if (this.partnerId) {
      items.set({
          name: SharedRoutes.DrivewealthAccountLiquidation,
          params: this.routeParams,
          query
        }, "Liquidation");
    }
    return items;
  }

  get indexOfCurrentTab () {
    return Array.from(this.navItems.keys()).findIndex(i => i.name === this.parentTabRoute());
  }

  getLinkedRouteForTabName (tab: string) {
    for (const [route, tabName] of this.navItems.entries()) {
      if (tabName === tab) {
        return route.name;
      }
    }
  }
  parentTabRoute () {
    // activity history has two sub tabs, only one of the rotues is in the map
    if (this.$route.name === SharedRoutes.DrivewealthInvestmentActivityHistoryV2
      || this.$route.name === SharedRoutes.DrivewealthInvestmentActivityTransfersV2
      || this.$route.name === SharedRoutes.DrivewealthInvestmentActivityTransfers) {
      return this.getLinkedRouteForTabName("Activity");
    }

    return this.$route.name;
  }

  mounted () {
    if (this.accountId) {
      this.drivewealthStore.loadManagedTransfersActivityByAccount(
          { username: this.username, partnerId: this.partnerId, accountId: this.accountId });
    } else {
      this.drivewealthStore.loadManagedTransfersActivity({ username: this.username, partnerId: this.partnerId });
    }
    this.investmentThresholdStore.loadInvestmentThresholds({ username: this.username, partnerId: this.partnerId });
  }

  get showAvailableInvestmentsTab (): boolean {
    return this.drivewealthStore.isAccountManagementType(DrivewealthAccountManagementType.Advisory, this.accountId)
      || this.drivewealthStore.isAccountManagementType(DrivewealthAccountManagementType.OpenBrokerage, this.accountId);
  }

  get isLoaded (): boolean {
    return this.drivewealthStore.managedTransfersActivityLoaded;
  }
}
